import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Bell, Play, Clock, Loader2, Users, Calendar,
  Trophy, Heart, MapPin, Shield, ChevronRight, Newspaper,
  BellRing, CheckCircle2, Eye, Star, Target
} from 'lucide-react';
import { parseTeamsWithFlags } from '../utils/teamFlags';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Sticky Navigation Tabs ────────────────────────────────────────
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'matches', label: 'Matches' },
  { id: 'standings', label: 'Standings' },
  { id: 'highlights', label: 'Highlights' },
  { id: 'replays', label: 'Replays' },
  { id: 'teams', label: 'Teams' },
  { id: 'news', label: 'News' },
];

// ─── Team Crest Component ──────────────────────────────────────────
function TeamCrest({ url, name, size = 'md' }) {
  const sizes = { sm: 'w-6 h-6', md: 'w-10 h-10', lg: 'w-14 h-14', xl: 'w-20 h-20' };
  return (
    <img
      src={url}
      alt={name}
      className={`${sizes[size]} rounded-full object-cover bg-white/10 ring-1 ring-white/10`}
      onError={(e) => {
        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=0d1a12&color=10b981&size=128&bold=true`;
      }}
    />
  );
}

// ─── Match Card (360×210, hover scale, personalization) ────────────
function MatchCard({ match, onWatch, onRemind, isReminded, isUserTeam }) {
  const homeTeam = match.home_team || {};
  const awayTeam = match.away_team || {};

  return (
    <div
      data-testid={`match-card-${match.id}`}
      className="w-[380px] h-[210px] flex-shrink-0 bg-[#1a2236] border border-white/5 rounded-xl p-5 hover:border-emerald-500/40 hover:shadow-xl hover:shadow-emerald-500/10 hover:scale-[1.03] transition-all duration-300 cursor-pointer group relative flex flex-col justify-between"
      onClick={() => onWatch(match)}
    >
      {/* Your Team Playing badge */}
      {isUserTeam && (
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 bg-emerald-500/15 border border-emerald-500/30 rounded-full">
          <Star className="w-3 h-3 text-emerald-400 fill-emerald-400" />
          <span className="text-emerald-400 text-[10px] font-bold uppercase tracking-wider">Your Team</span>
        </div>
      )}

      <div>
        {/* Stage + Live badge */}
        <div className="flex items-center gap-2 mb-3">
          {match.is_live && (
            <span className="flex items-center gap-1.5 px-2 py-0.5 bg-red-600 rounded text-white text-[10px] font-bold uppercase">
              <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" /></span>
              LIVE
            </span>
          )}
          {match.stage && (
            <span className="text-[10px] font-medium text-emerald-400/80 uppercase tracking-wider">{match.stage}</span>
          )}
        </div>

        {/* Teams row */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
            <TeamCrest url={homeTeam.crest_url} name={homeTeam.name || 'Home'} size="lg" />
            <span className="text-white text-xs font-semibold text-center leading-tight truncate w-full">{homeTeam.name || match.teams?.split(' vs ')[0]}</span>
          </div>

          <div className="text-center px-2 shrink-0">
            {match.is_live ? (
              <span className="text-2xl font-black text-white tabular-nums">
                {match.home_score ?? 0} <span className="text-white/30">:</span> {match.away_score ?? 0}
              </span>
            ) : match.start_time ? (
              <div className="flex flex-col items-center">
                <span className="text-xs text-gray-400">{formatMatchDate(match.start_time)}</span>
                <span className="text-sm font-bold text-white">{formatMatchHour(match.start_time)}</span>
              </div>
            ) : (
              <span className="text-lg font-bold text-white/40">VS</span>
            )}
          </div>

          <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
            <TeamCrest url={awayTeam.crest_url} name={awayTeam.name || 'Away'} size="lg" />
            <span className="text-white text-xs font-semibold text-center leading-tight truncate w-full">{awayTeam.name || match.teams?.split(' vs ')[1]}</span>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mt-auto pt-3">
        {match.is_live ? (
          <Button data-testid={`watch-live-${match.id}`} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs h-8 flex items-center justify-center gap-1.5" onClick={(e) => { e.stopPropagation(); onWatch(match); }}>
            <Play className="w-3.5 h-3.5" fill="currentColor" /> Watch Live
          </Button>
        ) : (
          <Button data-testid={`remind-me-${match.id}`} variant={isReminded ? "outline" : "default"} className={`flex-1 text-xs h-8 flex items-center justify-center gap-1.5 ${isReminded ? 'border-emerald-500 text-emerald-400' : 'bg-white/10 hover:bg-white/20 text-white'}`} onClick={(e) => { e.stopPropagation(); onRemind(match); }}>
            {isReminded ? <><CheckCircle2 className="w-3.5 h-3.5" /> Reminded</> : <><BellRing className="w-3.5 h-3.5" /> Remind Me</>}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Standings Table ───────────────────────────────────────────────
function StandingsTable({ group }) {
  return (
    <div data-testid={`standings-${group.group}`} className="bg-[#1a2236] rounded-xl overflow-hidden border border-white/5 mb-6">
      <div className="px-5 py-3 bg-emerald-600/10 border-b border-white/5">
        <h3 className="text-emerald-400 font-bold text-sm uppercase tracking-wider">{group.group}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 text-xs uppercase border-b border-white/5">
              <th className="text-left py-3 px-4 w-8">#</th>
              <th className="text-left py-3 px-2">Team</th>
              <th className="text-center py-3 px-2 w-10">P</th>
              <th className="text-center py-3 px-2 w-10">W</th>
              <th className="text-center py-3 px-2 w-10">D</th>
              <th className="text-center py-3 px-2 w-10">L</th>
              <th className="text-center py-3 px-2 w-12">GD</th>
              <th className="text-center py-3 px-2 w-12 font-bold">Pts</th>
            </tr>
          </thead>
          <tbody>
            {group.table.map((row, i) => (
              <tr key={row.team} className={`border-b border-white/5 last:border-0 ${row.position <= 2 ? 'bg-emerald-500/10 border-l-2 border-l-emerald-500' : row.position === 3 ? 'bg-yellow-500/10 border-l-2 border-l-yellow-500' : row.position === 4 ? 'bg-white/[0.03] border-l-2 border-l-gray-500/50' : ''} hover:bg-white/5 transition-colors`}>
                <td className="py-3 px-4 text-gray-400 font-medium">{row.position}</td>
                <td className="py-3 px-2">
                  <div className="flex items-center gap-2">
                    <TeamCrest url={row.crest_url} name={row.team} size="sm" />
                    <span className="text-white font-medium">{row.team}</span>
                    <span className="text-gray-500 text-xs hidden sm:inline">({row.code})</span>
                  </div>
                </td>
                <td className="text-center py-3 px-2 text-gray-300">{row.played}</td>
                <td className="text-center py-3 px-2 text-gray-300">{row.won}</td>
                <td className="text-center py-3 px-2 text-gray-300">{row.drawn}</td>
                <td className="text-center py-3 px-2 text-gray-300">{row.lost}</td>
                <td className="text-center py-3 px-2 text-gray-300">{row.gd > 0 ? `+${row.gd}` : row.gd}</td>
                <td className="text-center py-3 px-2 text-white font-bold">{row.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Video Card (Highlights & Replays) ─────────────────────────────
function VideoCard({ video, onClick, badge, badgeColor = 'bg-yellow-500 text-black' }) {
  return (
    <div data-testid={`video-card-${video.id}`} onClick={() => onClick(video)} className="group cursor-pointer">
      <div className="relative aspect-video rounded-xl overflow-hidden mb-3">
        <img src={video.thumbnail_url || video.thumbnail || 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=600'} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className={`absolute top-3 left-3 px-2 py-0.5 ${badgeColor} text-xs font-bold rounded`}>
          {badge || video.category || 'Video'}
        </div>
        {video.duration && (
          <div className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/80 text-white text-xs font-mono rounded">
            {formatDuration(video.duration)}
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-emerald-600 group-hover:scale-110 transition-all duration-300">
            <Play className="w-5 h-5 text-white fill-current ml-0.5" />
          </div>
        </div>
      </div>
      <h3 className="text-white font-semibold text-sm group-hover:text-emerald-400 transition-colors line-clamp-2">{video.title}</h3>
    </div>
  );
}

// ─── News Card ─────────────────────────────────────────────────────
function NewsCard({ article }) {
  return (
    <div data-testid={`news-${article.id}`} className="flex gap-4 bg-[#1a2236] rounded-xl p-4 border border-white/5 hover:border-emerald-500/20 transition-all cursor-pointer group">
      {article.image_url && (
        <img src={article.image_url} alt="" className="w-24 h-24 sm:w-32 sm:h-24 rounded-lg object-cover flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <span className="text-emerald-400 text-xs font-semibold uppercase tracking-wider">{article.category}</span>
        <h3 className="text-white font-semibold mt-1 mb-1 group-hover:text-emerald-400 transition-colors line-clamp-2 text-sm">{article.title}</h3>
        <p className="text-gray-400 text-xs line-clamp-2">{article.summary}</p>
        <div className="flex items-center gap-3 mt-2 text-gray-500 text-xs">
          <span>{article.author}</span>
          <span>{new Date(article.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Team Grid Item ────────────────────────────────────────────────
function TeamGridItem({ team, onClick }) {
  return (
    <div data-testid={`team-${team.code}`} onClick={onClick} className="flex flex-col items-center gap-3 p-4 bg-[#1a2236] rounded-xl border border-white/5 hover:border-emerald-500/20 hover:bg-[#1e2844] transition-all cursor-pointer group">
      <TeamCrest url={team.crest_url} name={team.name} size="xl" />
      <div className="text-center">
        <div className="text-white font-semibold text-sm group-hover:text-emerald-400 transition-colors">{team.name}</div>
        <div className="text-gray-500 text-xs">{team.group ? `Group ${team.group}` : team.country || ''}</div>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────
function formatMatchTime(datetime) {
  if (!datetime) return '';
  const now = new Date();
  const d = new Date(datetime);
  const isToday = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  const isTmrw = d.toDateString() === tomorrow.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (isToday) return `Today ${time}`;
  if (isTmrw) return `Tomorrow ${time}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${time}`;
}

function formatMatchDate(datetime) {
  if (!datetime) return '';
  const d = new Date(datetime);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const tmrw = new Date(now); tmrw.setDate(tmrw.getDate() + 1);
  if (d.toDateString() === tmrw.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMatchHour(datetime) {
  if (!datetime) return '';
  return new Date(datetime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatDuration(seconds) {
  if (!seconds) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function CompetitionDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [competition, setCompetition] = useState(null);
  const [matches, setMatches] = useState([]);
  const [videos, setVideos] = useState([]);
  const [standings, setStandings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [news, setNews] = useState([]);
  const [followedTeams, setFollowedTeams] = useState([]);
  const [remindedMatches, setRemindedMatches] = useState(new Set());
  const [activeTab, setActiveTab] = useState('overview');
  const tabsRef = useRef(null);
  const sectionRefs = useRef({});

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  // Check if match involves a followed team
  const isUserTeamMatch = useCallback((match) => {
    if (!followedTeams.length) return false;
    const teamsStr = (match.teams || '').toLowerCase();
    const homeName = (match.home_team?.name || '').toLowerCase();
    const awayName = (match.away_team?.name || '').toLowerCase();
    return followedTeams.some(ft => teamsStr.includes(ft) || homeName.includes(ft) || awayName.includes(ft));
  }, [followedTeams]);

  // Sort matches: user's team first, then by start_time
  const sortWithPersonalization = useCallback((matchList) => {
    return [...matchList].sort((a, b) => {
      const aUser = isUserTeamMatch(a) ? 0 : 1;
      const bUser = isUserTeamMatch(b) ? 0 : 1;
      if (aUser !== bUser) return aUser - bUser;
      return new Date(a.start_time || 0) - new Date(b.start_time || 0);
    });
  }, [isUserTeamMatch]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const compRes = await axios.get(`${API}/competitions/${slug}`);
      setCompetition(compRes.data);

      const [matchRes, videoRes, standingsRes, teamsRes, newsRes] = await Promise.allSettled([
        axios.get(`${API}/competitions/${slug}/matches`),
        axios.get(`${API}/competitions/${slug}/videos`),
        axios.get(`${API}/competitions/${slug}/standings`),
        axios.get(`${API}/competitions/${slug}/teams`),
        axios.get(`${API}/competitions/${slug}/news`),
      ]);

      if (matchRes.status === 'fulfilled') setMatches(matchRes.value.data.matches || []);
      if (videoRes.status === 'fulfilled') setVideos(videoRes.value.data.videos || []);
      if (standingsRes.status === 'fulfilled') setStandings(standingsRes.value.data.standings || []);
      if (teamsRes.status === 'fulfilled') setTeams(teamsRes.value.data.teams || []);
      if (newsRes.status === 'fulfilled') setNews(newsRes.value.data.news || []);
    } catch (err) {
      console.error('Failed to load competition:', err);
      setCompetition({ name: slug.toUpperCase(), description: 'Competition details loading...', status: 'active' });
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // Check follow + followed teams
  const fetchUserData = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(`${API}/user/following`, { headers, withCredentials: true });
      const comps = (res.data.competitions || []).map(c => c.toLowerCase());
      setIsFollowing(comps.includes(slug.toLowerCase()));
      setFollowedTeams((res.data.teams || []).map(t => t.toLowerCase()));
    } catch { /* not logged in */ }
  }, [slug, token]);

  useEffect(() => { fetchData(); fetchUserData(); }, [fetchData, fetchUserData]);

  // Toggle follow
  const toggleFollow = async () => {
    if (!token) { navigate('/login'); return; }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const endpoint = isFollowing ? 'unfollow-competition' : 'follow-competition';
      await axios.post(`${API}/user/${endpoint}`, { competition_slug: slug }, { headers, withCredentials: true });
      setIsFollowing(!isFollowing);
    } catch { /* silent */ }
  };

  const handleRemind = async (match) => {
    if (!token) { navigate('/login'); return; }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(`${API}/reminders/set`, { content_id: match.id }, { headers, withCredentials: true });
      setRemindedMatches(prev => new Set([...prev, match.id]));
    } catch { /* silent */ }
  };

  const handleWatch = (item) => item.id && navigate(item.is_live ? `/match/${item.id}` : `/watch/${item.id}`);

  // Derived data — deduplicated
  const liveMatches = sortWithPersonalization(matches.filter(m => m.is_live));
  const upcomingMatches = sortWithPersonalization(matches.filter(m => !m.is_live && m.start_time && new Date(m.start_time) > new Date()));
  const pastMatches = sortWithPersonalization(matches.filter(m => !m.is_live && (!m.start_time || new Date(m.start_time) <= new Date())));
  const highlights = videos.filter(v => v.category === 'highlight');
  const replays = videos.filter(v => v.category !== 'highlight');

  // Hero match: first live match, or first upcoming match
  const heroMatch = liveMatches[0] || upcomingMatches[0] || null;
  const heroMatchParsed = heroMatch ? parseTeamsWithFlags(heroMatch.teams) : null;

  // Scroll to section
  const scrollToSection = (tabId) => {
    setActiveTab(tabId);
    const el = sectionRefs.current[tabId];
    if (el) {
      const offset = 140;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1e]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="min-h-screen bg-[#0a0f1e]">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-[80vh] text-white">
          <Trophy className="w-16 h-16 text-gray-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Competition Not Found</h2>
          <Button onClick={() => navigate('/competitions')} className="mt-4">Back to Competitions</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1e]">
      <Navbar />

      {/* ─── HERO SECTION WITH NEXT/LIVE MATCH ────────────────── */}
      <div className="relative pt-20 pb-10 overflow-hidden">
        {competition.hero_image && (
          <div className="absolute inset-0">
            <img src={competition.hero_image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f1e] via-[#0a0f1e]/90 to-[#0a0f1e]/60" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1e] via-transparent to-transparent" />
          </div>
        )}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <button onClick={() => navigate('/competitions')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm" data-testid="back-button">
            <ArrowLeft className="w-4 h-4" /> Back to Competitions
          </button>

          <div className="flex flex-col lg:flex-row justify-between items-start gap-8">
            {/* Left: Competition info */}
            <div className="flex-1">
              {competition.live_matches > 0 && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-600/90 text-white text-xs font-bold rounded-full backdrop-blur-sm">
                    <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-white" /></span>
                    {competition.live_matches} LIVE {competition.live_matches === 1 ? 'MATCH' : 'MATCHES'}
                  </span>
                </motion.div>
              )}

              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl sm:text-5xl font-black text-white mb-3 tracking-tight">
                {competition.short_name || competition.name}
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-base text-gray-300 mb-5 max-w-xl leading-relaxed">
                {competition.description}
              </motion.p>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex flex-wrap items-center gap-3 mb-6">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 text-emerald-400 text-xs font-semibold rounded-full border border-emerald-500/20">
                  <Trophy className="w-3 h-3" />
                  {competition.category === 'continental' ? 'Continental' : 'Domestic League'}
                </span>
                {competition.teams_count && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 text-gray-300 text-xs rounded-full border border-white/10">
                    <Users className="w-3 h-3" /> {competition.teams_count} Teams
                  </span>
                )}
                {competition.country && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 text-gray-300 text-xs rounded-full border border-white/10">
                    <MapPin className="w-3 h-3" /> {competition.country}
                  </span>
                )}
              </motion.div>

              {/* CTA buttons */}
              <div className="flex gap-3">
                {heroMatch?.is_live && (
                  <Button
                    data-testid="hero-watch-live"
                    onClick={() => handleWatch(heroMatch)}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-semibold flex items-center gap-2"
                  >
                    <Play className="w-4 h-4" fill="currentColor" /> Watch Live
                  </Button>
                )}
                <Button
                  data-testid="follow-competition-btn"
                  onClick={toggleFollow}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    isFollowing ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${isFollowing ? 'fill-current' : ''}`} />
                  {isFollowing ? 'Following' : 'Follow Competition'}
                </Button>
              </div>
            </div>

            {/* Right: Hero Match Card */}
            {heroMatch && (
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="w-full lg:w-[400px] bg-[#111a30]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-6 cursor-pointer hover:border-emerald-500/30 hover:scale-[1.02] transition-all duration-300"
                onClick={() => handleWatch(heroMatch)}
                data-testid="hero-match-card"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                  <span className="text-gray-400 text-xs font-medium uppercase tracking-wider">
                    {heroMatch.is_live ? 'Live Now' : 'Next Match'}
                  </span>
                  {heroMatch.is_live ? (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 bg-red-600 rounded-full text-white text-[10px] font-bold uppercase">
                      <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" /></span>
                      LIVE
                    </span>
                  ) : heroMatch.start_time && (
                    <span className="text-emerald-400 text-xs font-medium">{formatMatchTime(heroMatch.start_time)}</span>
                  )}
                </div>

                {/* Teams with flags */}
                {heroMatchParsed ? (
                  <div className="flex items-center justify-between gap-3 mb-5">
                    <div className="flex flex-col items-center gap-2 flex-1">
                      {heroMatchParsed.home.flag ? (
                        <span className="text-4xl">{heroMatchParsed.home.flag}</span>
                      ) : (
                        <TeamCrest url={heroMatch.home_team?.crest_url} name={heroMatchParsed.home.name} size="lg" />
                      )}
                      <span className="text-white text-sm font-bold text-center">{heroMatchParsed.home.name}</span>
                    </div>
                    <div className="text-center px-2">
                      {heroMatch.is_live ? (
                        <span className="text-3xl font-black text-white tabular-nums">
                          {heroMatch.home_score ?? 0} <span className="text-white/30">:</span> {heroMatch.away_score ?? 0}
                        </span>
                      ) : (
                        <span className="text-xl font-bold text-white/30">VS</span>
                      )}
                    </div>
                    <div className="flex flex-col items-center gap-2 flex-1">
                      {heroMatchParsed.away.flag ? (
                        <span className="text-4xl">{heroMatchParsed.away.flag}</span>
                      ) : (
                        <TeamCrest url={heroMatch.away_team?.crest_url} name={heroMatchParsed.away.name} size="lg" />
                      )}
                      <span className="text-white text-sm font-bold text-center">{heroMatchParsed.away.name}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-white font-bold text-lg text-center mb-5">{heroMatch.teams || heroMatch.title}</p>
                )}

                {/* Stage */}
                {heroMatch.stage && (
                  <p className="text-center text-emerald-400/70 text-xs uppercase tracking-wider mb-4">{heroMatch.stage}</p>
                )}

                {/* CTA */}
                <Button
                  className={`w-full h-10 text-sm font-semibold rounded-lg flex items-center justify-center gap-2 ${
                    heroMatch.is_live ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                  onClick={(e) => { e.stopPropagation(); handleWatch(heroMatch); }}
                >
                  <Play className="w-4 h-4" fill="currentColor" />
                  {heroMatch.is_live ? 'Watch Live' : 'Set Reminder'}
                </Button>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* ─── STICKY TABS ────────────────────────────────────────── */}
      <div ref={tabsRef} className="sticky top-16 z-30 bg-[#0a0f1e]/95 backdrop-blur-xl border-b border-white/5" data-testid="competition-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide py-1">
            {TABS.map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => scrollToSection(tab.id)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap rounded-lg transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'text-emerald-400 bg-emerald-500/10'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ─── CONTENT ────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">

        {/* ── OVERVIEW: Live Now ─────────────────────────────────── */}
        <section ref={el => sectionRefs.current['overview'] = el} data-testid="section-overview">
          {liveMatches.length > 0 && (
            <div className="mb-10">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" /></span>
                Live Now
              </h2>
              <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
                <div className="flex gap-4 pb-2">
                  {liveMatches.map(match => (
                    <MatchCard key={match.id} match={match} onWatch={handleWatch} onRemind={handleRemind} isReminded={remindedMatches.has(match.id)} isUserTeam={isUserTeamMatch(match)} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Upcoming Matches */}
          {upcomingMatches.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Upcoming Matches
              </h2>
              <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
                <div className="flex gap-4 pb-2">
                  {upcomingMatches.map(match => (
                    <MatchCard key={match.id} match={match} onWatch={handleWatch} onRemind={handleRemind} isReminded={remindedMatches.has(match.id)} isUserTeam={isUserTeamMatch(match)} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* ── MATCHES (All — no duplicates) ─────────────────────── */}
        <section ref={el => sectionRefs.current['matches'] = el} data-testid="section-matches">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-400" /> All Matches</h2>
          {matches.length === 0 ? (
            <p className="text-gray-500 text-sm">No matches scheduled yet.</p>
          ) : (
            <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
              <div className="flex gap-4 pb-2 flex-wrap lg:flex-nowrap">
                {sortWithPersonalization(matches.filter(m => !m.is_live)).map(match => (
                  <MatchCard key={match.id} match={match} onWatch={handleWatch} onRemind={handleRemind} isReminded={remindedMatches.has(match.id)} isUserTeam={isUserTeamMatch(match)} />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ── STANDINGS ────────────────────────────────────────── */}
        <section ref={el => sectionRefs.current['standings'] = el} data-testid="section-standings">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-emerald-400" /> Standings</h2>
          {standings.length === 0 ? (
            <p className="text-gray-500 text-sm">Standings not yet available.</p>
          ) : (
            <>
              <div className="grid gap-6 lg:grid-cols-2">
                {standings.map(group => (
                  <StandingsTable key={group.group} group={group} />
                ))}
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-gray-400 mt-4 px-1">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500/20 border border-emerald-500/40" /> Qualified (1st–2nd)</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-yellow-500/20 border border-yellow-500/40" /> Playoff (3rd)</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-white/5 border border-gray-500/30" /> Eliminated (4th)</span>
              </div>
            </>
          )}
        </section>

        {/* ── HIGHLIGHTS ───────────────────────────────────────── */}
        <section ref={el => sectionRefs.current['highlights'] = el} data-testid="section-highlights">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Target className="w-5 h-5 text-yellow-500" /> Highlights</h2>
          {highlights.length === 0 ? (
            <p className="text-gray-500 text-sm">No highlights available yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {highlights.map(v => (
                <VideoCard key={v.id} video={v} onClick={handleWatch} badge="Highlight" badgeColor="bg-yellow-500 text-black" />
              ))}
            </div>
          )}
        </section>

        {/* ── REPLAYS ──────────────────────────────────────────── */}
        <section ref={el => sectionRefs.current['replays'] = el} data-testid="section-replays">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Play className="w-5 h-5 text-emerald-400" /> Full Match Replays</h2>
          {replays.length === 0 ? (
            <p className="text-gray-500 text-sm">No replays available yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {replays.map(v => (
                <VideoCard key={v.id} video={v} onClick={handleWatch} badge="Replay" badgeColor="bg-emerald-600 text-white" />
              ))}
            </div>
          )}
        </section>

        {/* ── TEAMS ────────────────────────────────────────────── */}
        <section ref={el => sectionRefs.current['teams'] = el} data-testid="section-teams">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Shield className="w-5 h-5 text-emerald-400" /> Teams</h2>
          {teams.length === 0 ? (
            <p className="text-gray-500 text-sm">Team information not available.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {teams.map(team => (
                <TeamGridItem key={team.code} team={team} onClick={() => navigate(`/team/${team.name.toLowerCase().replace(/\s+/g, '-')}`)} />
              ))}
            </div>
          )}
        </section>

        {/* ── NEWS ─────────────────────────────────────────────── */}
        <section ref={el => sectionRefs.current['news'] = el} data-testid="section-news">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Newspaper className="w-5 h-5 text-emerald-400" /> News</h2>
          {news.length === 0 ? (
            <p className="text-gray-500 text-sm">No news available yet.</p>
          ) : (
            <div className="grid gap-4">
              {news.map(article => (
                <NewsCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </section>

      </div>
    </div>
  );
}
